import React, { useState, useEffect, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import { Pagination, message } from 'antd'
import useTitle from '../../../../hooks/useTitle'
import styles from './index.module.scss'
import request from '../../../../network/request'
import OrderItem from '../components/OrderItem'
import OrderType from '../components/OrderType'
import { Placeholder } from '../components/Placeholder'
import Skeleton from '../components/Skeleton'
import useOrderSearchQuery from '../store/useOrderSearchQuery'
import useOrderType from '../store/useOrderType'

// 最多存 10 条记录
const MAX_HIS_LEN = 10
const PAGE_SIZE = 5

export default function OrderSearch() {
  useTitle('服务单搜索')

  const { orderSearchQuery, updatePage, updateKeyword } = useOrderSearchQuery()
  const { orderType } = useOrderType()
  const history = useHistory()
  const [searchHistory, setSearchHistory] = useState<string[]>([])
  const [keyword, setKeyword] = useState(orderSearchQuery.keyword)
  const [orderList, setOrderList] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const pageIndexRef = useRef(orderSearchQuery.pageIndex)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [isSearched, setIsSearched] = useState(false)

  useEffect(() => {
    let searchHisFromStorge = localStorage.getItem('__ss_ms_search_history')
    if (searchHisFromStorge) {
      searchHisFromStorge = JSON.parse(searchHisFromStorge)
      if (Array.isArray(searchHisFromStorge)) {
        setSearchHistory(searchHisFromStorge)
      }
    }

    return () => {
      message.destroy()
    }
  }, [])

  useEffect(() => {
    if (orderSearchQuery?.keyword?.length > 0) {
      setIsSearched(true)
      pageIndexRef.current = 1
      getOrderList()
    }
  }, [orderType])

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault()

    const kwStr = keyword.trim()

    if (kwStr.length > 0) {
      await getOrderList()

      // 处理搜索历史
      let tmp = [...searchHistory]
      tmp = tmp.filter((item) => item !== kwStr)

      if (tmp.length === MAX_HIS_LEN) {
        tmp.pop()
      }
      tmp.unshift(kwStr)
      localStorage.setItem('__ss_ms_search_history', JSON.stringify(tmp))
    }
  }

  const getOrderList = async (kw?: string) => {
    setIsLoading(true)
    const loaded = message.loading('搜索中...', 0)
    try {
      const { total, records }: any = await request.get(
        '/dragon/service/order/listPage',
        {
          params: {
            orderStatisticsType: 'all',
            pageSize: PAGE_SIZE,
            pageIndex: pageIndexRef.current,
            query: kw || keyword,
            orderType: orderType.default
          },
        }
      )
      setTotal(total)
      setOrderList(records)
    } catch (err) {
      setIsError(true)
    } finally {
      const eleActive = document.activeElement as any
      eleActive.blur()
      setIsSearched(true)
      loaded()
      setIsLoading(false)
    }
  }

  const handleChange = (event: any) => {
    setKeyword(event.target.value)
    updateKeyword(event.target.value)
  }

  const handleClearHis = () => {
    localStorage.setItem('__ss_ms_search_history', '')
    setSearchHistory([])
  }

  const handleDeleteHis = (index: number) => {
    const tmp = [...searchHistory]
    tmp.splice(index, 1)
    localStorage.setItem('__ss_ms_search_history', JSON.stringify(tmp))
    setSearchHistory(tmp)
  }

  const handleCancelSearch = () => {
    history.goBack()
  }

  const handleChangePage = (page: number) => {
    pageIndexRef.current = page

    updatePage(page)

    getOrderList()
  }

  const handleClickHis = (his: string) => {
    setKeyword(his)

    updateKeyword(his)

    getOrderList(his)
  }

  const handleRefresh = () => {
    getOrderList()
  }

  const handleClearKeywordInput = () => {
    setKeyword('')
    updateKeyword('')
  }

  return (
    <div className={styles.pageSearch}>
      <form
        action=""
        className={styles.header}
        onSubmit={(e) => handleSearch(e)}
      >
        <div className={styles.searchWrap}>
          <div className={styles.searchIcon}></div>
          <input
            type="search"
            value={keyword}
            className={styles.searchInput}
            onChange={(e) => handleChange(e)}
            placeholder="VIN / 车牌 / 手机号 / 订单号"
          />
          {keyword.length > 0 && (
            <div
              className={styles.clearIcon}
              onClick={handleClearKeywordInput}
            ></div>
          )}
        </div>
        <button
          type="button"
          className={styles.cancelBtn}
          onClick={handleCancelSearch}
        >
          取消
        </button>
      </form>
      {searchHistory.length > 0 && !isSearched && (
        <div className={styles.searchHistoryWrap}>
          <div className={styles.titleWrap}>
            <p>搜索记录</p>
            <div onClick={handleClearHis}>清空</div>
          </div>
          {searchHistory.map((history: string, hisIndex: number) => {
            return (
              <div className={styles.hisItem} key={hisIndex}>
                <p
                  className={styles.historyTxt}
                  onClick={() => handleClickHis(history)}
                >
                  {history}
                </p>
                <div
                  className={styles.deleteIcon}
                  onClick={() => {
                    handleDeleteHis(hisIndex)
                  }}
                ></div>
              </div>
            )
          })}
        </div>
      )}
      {isSearched && (
        <>
          {isError ? (
            <Placeholder type="error"></Placeholder>
          ) : (
            <>
              {orderList.length > 0 ? (
                <div className={styles.contentWrap}>
                  <OrderType></OrderType>
                  {orderList.map((item) => {
                    return (
                      <OrderItem
                        key={item.id}
                        orderInfo={item}
                        onRefresh={handleRefresh}
                      ></OrderItem>
                    )
                  })}
                  <div className={styles.paginationWrap}>
                    {total > PAGE_SIZE && (
                      <Pagination
                        current={pageIndexRef.current}
                        total={total}
                        pageSize={PAGE_SIZE}
                        onChange={handleChangePage}
                        size="default"
                      />
                    )}
                  </div>
                </div>
              ) : isLoading ? (
                <Skeleton></Skeleton>
              ) : (
                <Placeholder
                  type="empty"
                  title="搜索结果为空"
                  subTitle="请更换搜索关键词, 支持手机号，车牌及订单号搜索"
                ></Placeholder>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}
